import { ILatLng } from 'src/app/core/models/google-map-models';
import { Enums, DISTANCE_TYPE } from '../enums/route.enum';
import { SummaryData } from './route-summary-model';
import { AAConstant } from 'src/app/core/models/constants';
import { RouteSignpostList } from './route-signposts-model';
import { PrintModel } from './route-print-model';
import { GoogleMapOptionsModel } from '../../google-map/models/google-map-model';
import { AARouterURL } from '../../app-routing/enums/routing.enum';

export class AALocationModel {
        PlaceId: string;
        Name: string;
        ActualLocation: string;
        RouteType: Enums.RouteType;
        Latitude: number;
        Longitude: number;
        PredictionList: Array<any> = [];

        hasError: boolean;
        errorMessage: string;
        predictionListItem: any;

        isLoading = false;
        isPredictionListOpen = false;

        formatted_address: string;
        partial_match: boolean;

        // flag via route applyed on google map
        isNewRoute = false;

        constructor(name: string,
                    latitude?: number,
                    longitude?: number,
                    type?: Enums.RouteType,
                    formatted_Address?: string,
                    partial_match?: boolean
                    ) {
                this.Name = name;
                this.Latitude = (latitude) ? latitude : 0;
                this.Longitude = (longitude) ? longitude : 0;
                this.RouteType = type;
                this.formatted_address = formatted_Address;
                this.partial_match = partial_match;
        }

        public apply(location: AALocationModel) {
                this.Name = location.Name;
                this.Latitude = location.Latitude;
                this.Longitude = location.Longitude;
                this.RouteType = location.RouteType;
                this.PlaceId = location.PlaceId;
                this.PredictionList = location.PredictionList;
        }

        public setName(name: string, override?: boolean): void {
                if (name && this.Name !== name || override) {
                        this.Name = name;
                        this.clear();
                }
        }

        public nameHasLatLng(): boolean {
                if (this.validName()) {
                        const latLng: ILatLng = {lat: null, lng: null};
                        latLng.lat = Number(this.Name.split(',')[0]);
                        latLng.lng = Number(this.Name.split(',')[1]);

                        if ( (latLng.lat > -90 || latLng.lat < 90) && (latLng.lng > -180 || latLng.lng < 180) ) {
                                this.latLng = latLng;
                                return true;
                        }
                }

                return false;
        }

        public setAsCurrentLocation() {
                this.setName( AAConstant.CURRENT_LOCATION );
        }

        public setupPredictionList(list: Array<any>) {
                this.PredictionList = list;
        }

        set latLng(latLng: ILatLng) {
                this.Latitude = latLng.lat;
                this.Longitude = latLng.lng;
        }

        get latLng(): ILatLng {
                return {lat: this.Latitude, lng: this.Longitude};
        }

        public isCurrentLocation(): boolean {
                return (this.Name == AAConstant.CURRENT_LOCATION);
        }

        public reset() {
                this.Name = '';
                this.clear();
        }

        public clear() {
                this.Latitude = 0;
                this.Longitude = 0;
                this.PlaceId = '';
        }

        public validName(minLength: number = AAConstant.MIN_CHARS_ENTRY_FOR_TRAFFIC_NEWS_SEARCH): boolean {
                return (this.Name != '' && this.Name.length >= minLength);
        }

        public valid(): boolean {
                return (this.Name != ''
                        && (this.Latitude && this.Latitude !== 0)
                        && (this.Longitude && this.Longitude !== 0) );
        }

        public clearPredictionList(keepFirstItem: boolean= false, ind: number= 0) {
                if (this.PredictionList !== undefined && this.PredictionList.length && keepFirstItem) {
                        this.predictionListItem = this.PredictionList[ind];
                }
                this.PredictionList = [];
                this.isPredictionListOpen = false;
        }

        public validateLocationName(): void {
                if (this.predictionListItem && this.predictionListItem.description) {
                    this.clear();
                    this.Name = this.predictionListItem.description;
                    this.PlaceId = this.predictionListItem.place_id;
                    this.predictionListItem = null;
                }
        }
}

export class RoutePlannerDataModel {
        public FromLocation: AALocationModel = new AALocationModel('', 0, 0, Enums.RouteType.FromDestination);
        public ToLocation: AALocationModel = new AALocationModel('', 0, 0, Enums.RouteType.ToDestination);

        public ViaRoutes: Array<AALocationModel> = [];
        public DefaultPos: ILatLng = { lat: 54.522005, lng: -3.083582 };
        public OptionsData: RoutePlannerOptionsData = new RoutePlannerOptionsData();
        public SummaryData: RoutePlannerSummaryData = new RoutePlannerSummaryData();

        public RouteSignpostList: RouteSignpostList = new RouteSignpostList();
        public PrintModel: PrintModel = new PrintModel();

        public googleMapOptions: GoogleMapOptionsModel = new GoogleMapOptionsModel();

        constructor() { }

        resetAll() {
                this.SummaryData.reset();
                this.RouteSignpostList.reset();
                this.PrintModel.reset();
                this.OptionsData.reset();
                // this.googleMapOptions.reset();
        }

        public resetVia() {
                this.ViaRoutes = [];
        }
        public reverseVia() {
                this.ViaRoutes.reverse();
        }

        public resetLocation() {
                this.FromLocation.reset();
                this.ToLocation.reset();
        }

        public clearLocation() {
                this.FromLocation.clear();
                this.ToLocation.clear();
        }

        public reverse(): void {
                const  toLocation: any = {...this.ToLocation};
                this.ToLocation.apply(this.FromLocation);
                this.FromLocation.apply(toLocation);
        }

        public markViaRoutesAsUsed() {
                this.ViaRoutes.forEach(viaPoint => {
                        viaPoint.isNewRoute = false;
                });
        }
}

export class RoutePlannerOptionsData {
        public AvoidTolls = false;
        public AvoidHighways = false;
        public AvoidTraffic = false;

        public ToggleOptionsPanel = false;

        public encodeURL(): string {
                if (this.AvoidHighways || this.AvoidTolls || this.AvoidTraffic) {
                        const options: Array<any> = [this.AvoidHighways, this.AvoidTolls, this.AvoidTraffic];
                        return options.join(AARouterURL._SEPARATOR);
                }
                return '';
        }

        public decodeURL(options: any) {
                const optionsData: Array<string> = options.split(AARouterURL._SEPARATOR);

                if (optionsData.length) {
                        this.AvoidHighways = optionsData[0] == 'true';
                        this.AvoidTolls = optionsData[1] == 'true';
                        this.AvoidTraffic = optionsData[2] == 'true';
                }

                // to open panel by default when either of the value set true
                this.ToggleOptionsPanel = (this.AvoidHighways || this.AvoidTolls);
        }


        reset(): void {
                this.AvoidHighways = false;
                this.AvoidTolls = false;
                this.AvoidTraffic = false;
                this.ToggleOptionsPanel = false;
        }
}

export class RoutePlannerSummaryData {
        public SelectedAltRoute = 0;
        public DistanceType: DISTANCE_TYPE = DISTANCE_TYPE.MILES;

        public SummaryDataList: Array<SummaryData> = [];

        constructor() { }

        getSelectedSummaryData(): SummaryData {
                return this.SummaryDataList[this.SelectedAltRoute];
        }

        reset(): void {
              this.SummaryDataList = [];
        }
}
